import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  InputAdornment,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { CloudUpload as CloudUploadIcon } from '@mui/icons-material';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';
import { LegalDialog, type LegalDocType } from '@/components/Legal/LegalDialog';
import { authApi } from '@/api/auth';
import { normalizeError } from '@/api/client';
import {
  tenantRegistrationSchema,
  type TenantRegistrationFormValues,
} from '@/schemas';
import {
  AuthCard,
  AutocompleteField,
  FormSelect,
  FormTextField,
  PasswordField,
} from '@/components';
import { BUSINESS_TYPE_OPTIONS, INDUSTRY_OPTIONS } from '@/constants/options';
import { type SelectOption } from '@/components';
import { STATES, citiesOf, districtsOf } from '@/constants/india';
import { brand } from '@/theme/theme';
import type { TenantRegistrationRequest } from '@/types';

/**
 * Self-service registration.
 *
 * - No tenant code (server slugifies it from business name)
 * - No administrator block (server creates a default `admin` / `Admin@123`
 *   user; the login page surfaces these on the success banner)
 * - State / district / city are filterable autocompletes seeded from the
 *   bundled India dataset; districts and cities filter by the chosen state
 * - V9 added an optional Regulatory Licences section: Drug License #
 *   + expiry, Food (FSSAI) License # + expiry, plus an OPTIONAL Drug
 *   License certificate file upload. The certificate is uploaded
 *   separately right after JSON registration succeeds, using the
 *   one-time upload token in the registration response.
 * - Field heights deliberately small (size="small")
 */

/** 5 MB matches the controller-side ceiling — fail fast on the client. */
const MAX_CERT_BYTES = 5 * 1024 * 1024;
const ALLOWED_CERT_EXT = ['pdf', 'jpg', 'jpeg', 'png'] as const;

export function RegisterTenantPage() {
  const navigate = useNavigate();
  const [serverError, setServerError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [legalDoc, setLegalDoc] = useState<LegalDocType | null>(null);

  // Dynamic software vendor list from API filtered by selected industry
  const [vendorOptions, setVendorOptions] = useState<SelectOption[]>([
    { value: 'NONE', label: 'None' },
    { value: 'OTHERS', label: 'Others' },
  ]);

  // Username availability check
  const [usernameStatus, setUsernameStatus] = useState<'idle' | 'checking' | 'available' | 'taken'>('idle');
  const [usernameTakenMsg, setUsernameTakenMsg] = useState<string>('');
  const usernameCheckTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Drug License certificate file. Held in component state (not in the
  // form schema) because it's not validated by Zod and goes through a
  // separate multipart endpoint after JSON registration succeeds.
  const [dlCertFile, setDlCertFile] = useState<File | null>(null);
  const [dlCertError, setDlCertError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const {
    register,
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<TenantRegistrationFormValues>({
    resolver: zodResolver(tenantRegistrationSchema),
    defaultValues: {
      tenantName: '',
      tenantType: 'WHOLESALER',
      industry: 'PHARMACEUTICAL',
      gstin: '',
      vendorName: 'NONE',
      drugLicenseNumber: '',
      drugLicenseExpiry: '',
      foodLicenseNumber: '',
      foodLicenseExpiry: '',
      adminUsername: '',
      adminPassword: '',
      address: '',
      area: '',
      city: '',
      district: '',
      state: '',
      pincode: '',
      phone: '',
      tenantEmail: '',
      agreedToTerms: false,
    },
  });

  // Watch state so district/city options can react.
  const watchedState = useWatch({ control, name: 'state' }) ?? '';

  // Watch consent checkbox — Checkbox needs a controlled `checked` prop,
  // which register() alone doesn't provide.
  const watchedAgreedToTerms = useWatch({ control, name: 'agreedToTerms' }) ?? false;

  // Watch industry to load matching software vendors
  const watchedIndustry = useWatch({ control, name: 'industry' }) ?? '';
  useEffect(() => {
    authApi.listPublicSoftwareVendors(watchedIndustry || undefined).then(list => {
      setVendorOptions([
        { value: 'NONE', label: 'None' },
        ...list.map(v => ({ value: String(v.id), label: v.softwareName })),
        { value: 'OTHERS', label: 'Others' },
      ]);
    }).catch(() => {});
  }, [watchedIndustry]);

  // Watch username + tenantType for real-time availability check
  const watchedUsername = useWatch({ control, name: 'adminUsername' }) ?? '';
  const watchedTenantType = useWatch({ control, name: 'tenantType' }) ?? '';

  const checkUsername = useCallback((username: string, tenantType: string) => {
    if (!username || username.length < 3 || !tenantType) {
      setUsernameStatus('idle');
      return;
    }
    setUsernameStatus('checking');
    authApi.checkUsername(username, tenantType)
      .then(res => {
        if (res.available) {
          setUsernameStatus('available');
          setUsernameTakenMsg('');
        } else {
          setUsernameStatus('taken');
          setUsernameTakenMsg(res.message);
        }
      })
      .catch(() => setUsernameStatus('idle'));
  }, []);

  // Debounce: fire 600 ms after the user stops typing
  useEffect(() => {
    if (usernameCheckTimer.current) clearTimeout(usernameCheckTimer.current);
    if (!watchedUsername || watchedUsername.length < 3) {
      setUsernameStatus('idle');
      return;
    }
    usernameCheckTimer.current = setTimeout(() => {
      checkUsername(watchedUsername, watchedTenantType);
    }, 600);
    return () => {
      if (usernameCheckTimer.current) clearTimeout(usernameCheckTimer.current);
    };
  }, [watchedUsername, watchedTenantType, checkUsername]);
  const districtOptions = useMemo(() => districtsOf(watchedState), [watchedState]);
  const cityOptions = useMemo(() => citiesOf(watchedState), [watchedState]);

  // Reset district/city when state changes (skip the initial render).
  const previousState = useRef(watchedState);
  useEffect(() => {
    if (previousState.current !== watchedState) {
      previousState.current = watchedState;
      setValue('district', '', { shouldValidate: false });
      setValue('city', '', { shouldValidate: false });
    }
  }, [watchedState, setValue]);

  /**
   * File-input handler. Validates size + extension client-side so the
   * user gets immediate feedback instead of waiting for the upload
   * round-trip to fail.
   */
  const handleFilePicked = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDlCertError(null);
    const f = e.target.files?.[0] ?? null;
    if (!f) {
      setDlCertFile(null);
      return;
    }
    if (f.size > MAX_CERT_BYTES) {
      setDlCertError('File exceeds 5 MB limit.');
      setDlCertFile(null);
      return;
    }
    const ext = f.name.split('.').pop()?.toLowerCase();
    if (!ext || !ALLOWED_CERT_EXT.includes(ext as typeof ALLOWED_CERT_EXT[number])) {
      setDlCertError('Only PDF, JPG, JPEG and PNG files are allowed.');
      setDlCertFile(null);
      return;
    }
    setDlCertFile(f);
  };

  const clearFile = () => {
    setDlCertFile(null);
    setDlCertError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const onSubmit = async (values: TenantRegistrationFormValues) => {
    // Block submission if username is already taken
    if (usernameStatus === 'taken') {
      setServerError(usernameTakenMsg || 'Username is already taken. Please choose a different one.');
      return;
    }
    setServerError(null);
    setSubmitting(true);
    try {
      // Convert empty optional strings to undefined so the backend treats
      // them as null instead of validating empty inputs. agreedToTerms is a
      // client-side-only consent gate — the backend has no such field.
      const { agreedToTerms: _agreedToTerms, ...valuesWithoutConsent } = values;
      const basePayload: TenantRegistrationRequest = Object.fromEntries(
        Object.entries(valuesWithoutConsent).map(([k, v]) => [k, v === '' ? undefined : v])
      ) as unknown as TenantRegistrationRequest;

      // Transform vendorName: if it's a numeric ID (saved software vendor), use softwareVendorId
      const payload = { ...basePayload };
      const vendorVal = basePayload.vendorName;
      if (vendorVal && /^\d+$/.test(vendorVal)) {
        payload.softwareVendorId = Number(vendorVal);
        payload.vendorName = undefined;
      } else {
        payload.vendorName = vendorVal === 'NONE' ? undefined : vendorVal;
      }

      const response = await authApi.registerTenant(payload);

      // After JSON registration succeeds, chain the optional certificate
      // upload. We swallow upload errors with a console warning rather
      // than failing the whole flow — the user has been registered
      // successfully and can re-upload later from their profile.
      if (dlCertFile && response.uploadToken) {
        try {
          await authApi.uploadDlCertificate(
            response.tenantId,
            response.uploadToken,
            dlCertFile,
          );
        } catch (uploadErr) {
          // eslint-disable-next-line no-console
          console.warn(
            'Tenant registered but Drug License certificate upload failed:',
            uploadErr,
          );
        }
      }

      navigate('/login', {
        replace: true,
        state: {
          registered: true,
          // Server echoes back whatever username was used (the one the
          // user typed, since we now send it). Fall back to the local
          // value just in case.
          defaultUsername: response.adminUsername || values.adminUsername,
          // We only ever reveal the default password from the server when
          // the user did NOT pick one. If they typed their own, we don't
          // show it again on the login screen.
          defaultPassword: values.adminPassword
            ? undefined
            : response.adminDefaultPassword,
          businessType: values.tenantType,
        },
      });
    } catch (err) {
      const e = normalizeError(err);
      setServerError(e.message || 'Registration failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
    <AuthCard
      maxWidth={760}
      footer={
        <Typography variant="body2" sx={{ color: brand.inkSoft }}>
          Already have an account?{' '}
          <Link
            component="button"
            onClick={() => navigate('/login')}
            sx={{ fontWeight: 600, color: brand.violet, textDecoration: 'none' }}
          >
            Sign in
          </Link>
        </Typography>
      }
    >
      <Box sx={{ mb: 3 }}>
        <Typography variant="h2" sx={{ color: brand.navy, mb: 0.5 }}>
          Register your business
        </Typography>
        <Typography variant="body2" sx={{ color: brand.inkSoft }}>
          Choose a username and password — these are the credentials you'll
          use to sign in.
        </Typography>
      </Box>

      {serverError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {serverError}
        </Alert>
      )}

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <Stack spacing={3}>
          {/* ----- Business details ----- */}
          <Box>
            <Typography variant="h4" sx={{ mb: 1.5, color: brand.navy }}>
              Business details
            </Typography>
            <Grid container spacing={1.5}>
              <Grid item xs={12}>
                <FormTextField
                  name="tenantName"
                  register={register}
                  errors={errors}
                  label="Business name"
                  autoFocus
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormSelect
                  name="tenantType"
                  control={control}
                  errors={errors}
                  label="Business type"
                  options={[...BUSINESS_TYPE_OPTIONS]}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormSelect
                  name="industry"
                  control={control}
                  errors={errors}
                  label="Industry"
                  options={INDUSTRY_OPTIONS.filter(o => o.value === 'PHARMACEUTICAL')}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormTextField
                  name="phone"
                  register={register}
                  errors={errors}
                  label="Phone"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormTextField
                  name="gstin"
                  register={register}
                  errors={errors}
                  label="GSTIN (optional)"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormSelect
                  name="vendorName"
                  control={control}
                  errors={errors}
                  label="Accounting / ERP software"
                  options={vendorOptions}
                />
              </Grid>

              {/* ----- Login credentials (placed right after GSTIN) ----- */}
              <Grid item xs={12} sm={6}>
                <Box>
                  <FormTextField
                    name="adminUsername"
                    register={register}
                    errors={errors}
                    label="Username"
                    autoComplete="username"
                    error={usernameStatus === 'taken'}
                    helperText={
                      usernameStatus === 'taken'
                        ? usernameTakenMsg
                        : usernameStatus === 'available'
                        ? '✓ Username is available'
                        : undefined
                    }
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          {usernameStatus === 'checking' && (
                            <CircularProgress size={16} sx={{ color: '#9ca3af' }} />
                          )}
                          {usernameStatus === 'available' && (
                            <CheckCircleIcon sx={{ fontSize: 18, color: '#16a34a' }} />
                          )}
                          {usernameStatus === 'taken' && (
                            <CancelIcon sx={{ fontSize: 18, color: '#dc2626' }} />
                          )}
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiFormHelperText-root': {
                        color: usernameStatus === 'available' ? '#16a34a' : undefined,
                      },
                    }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <PasswordField
                  name="adminPassword"
                  register={register}
                  errors={errors}
                  label="Password"
                  autoComplete="new-password"
                />
              </Grid>

              <Grid item xs={12}>
                <FormTextField
                  name="tenantEmail"
                  register={register}
                  errors={errors}
                  label="Business email"
                  type="email"
                />
              </Grid>
            </Grid>
          </Box>

          <Divider />

          {/* ----- Regulatory licences (V9) -----
              All fields optional. Pharma tenants typically supply Drug
              License (number + expiry + scanned cert); food/F&B tenants
              the FSSAI Food License. Anything left blank is stored as
              null on the tenant row. */}
          <Box>
            <Typography variant="h4" sx={{ mb: 0.5, color: brand.navy }}>
              Regulatory licences (optional)
            </Typography>
            <Typography variant="caption" sx={{ color: brand.inkSoft, display: 'block', mb: 1.5 }}>
              Add the relevant licence numbers for your business. Pharma businesses usually have a Drug License; food & beverage businesses have an FSSAI / Food License.
            </Typography>
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6}>
                <FormTextField
                  name="drugLicenseNumber"
                  register={register}
                  errors={errors}
                  label="Drug License Number"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormTextField
                  name="drugLicenseExpiry"
                  register={register}
                  errors={errors}
                  label="Drug License Expiry"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormTextField
                  name="foodLicenseNumber"
                  register={register}
                  errors={errors}
                  label="Food License Number (FSSAI)"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormTextField
                  name="foodLicenseExpiry"
                  register={register}
                  errors={errors}
                  label="Food License Expiry"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              {/* Drug License certificate upload — also optional. Held
                  outside the form schema; chained to the registration
                  call after the JSON request succeeds. */}
              <Grid item xs={12}>
                <Box
                  sx={{
                    border: `1px dashed ${brand.glassBorder ?? '#D9DEEA'}`,
                    borderRadius: 1.5,
                    p: 1.5,
                    background: '#FBFBFD',
                  }}
                >
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1.5}
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                  >
                    <Button
                      variant="outlined"
                      component="label"
                      size="small"
                      startIcon={<CloudUploadIcon fontSize="small" />}
                    >
                      {dlCertFile ? 'Replace certificate' : 'Upload Drug License certificate'}
                      <input
                        ref={fileInputRef}
                        type="file"
                        hidden
                        accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                        onChange={handleFilePicked}
                      />
                    </Button>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      {dlCertFile ? (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography
                            variant="body2"
                            sx={{
                              color: brand.navy,
                              fontWeight: 600,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {dlCertFile.name}
                          </Typography>
                          <Typography variant="caption" sx={{ color: brand.inkSoft }}>
                            ({(dlCertFile.size / 1024).toFixed(0)} KB)
                          </Typography>
                          <Link
                            component="button"
                            type="button"
                            onClick={clearFile}
                            sx={{ fontSize: 12, ml: 1 }}
                          >
                            Remove
                          </Link>
                        </Stack>
                      ) : (
                        <Typography variant="caption" sx={{ color: brand.inkSoft }}>
                          PDF, JPG or PNG — up to 5 MB. Skip this if you don't have a Drug License or want to upload it later.
                        </Typography>
                      )}
                      {dlCertError && (
                        <Typography
                          variant="caption"
                          sx={{ color: 'error.main', display: 'block', mt: 0.5 }}
                        >
                          {dlCertError}
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                </Box>
              </Grid>
            </Grid>
          </Box>

          <Divider />

          {/* ----- Address ----- */}
          <Box>
            <Typography variant="h4" sx={{ mb: 1.5, color: brand.navy }}>
              Address
            </Typography>
            <Grid container spacing={1.5}>
              <Grid item xs={12}>
                <FormTextField
                  name="address"
                  register={register}
                  errors={errors}
                  label="Street address"
                  multiline
                  minRows={2}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <AutocompleteField
                  name="state"
                  control={control}
                  errors={errors}
                  label="State"
                  options={STATES}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <AutocompleteField
                  name="district"
                  control={control}
                  errors={errors}
                  label="District"
                  options={districtOptions}
                  disabled={!watchedState}
                  helperText={!watchedState ? 'Choose a state first' : undefined}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormTextField
                  name="area"
                  register={register}
                  errors={errors}
                  label="Area / locality"
                  helperText="Your area will be used to show nearby suppliers to customers"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <AutocompleteField
                  name="city"
                  control={control}
                  errors={errors}
                  label="City / town"
                  options={cityOptions}
                  disabled={!watchedState}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormTextField
                  name="pincode"
                  register={register}
                  errors={errors}
                  label="Pincode"
                />
              </Grid>
            </Grid>
          </Box>

          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  {...register('agreedToTerms')}
                  checked={watchedAgreedToTerms}
                />
              }
              label={
                <Typography variant="body2" sx={{ color: brand.ink }}>
                  I agree to the{' '}
                  <Link
                    component="button"
                    type="button"
                    onClick={(e) => { e.preventDefault(); setLegalDoc('terms'); }}
                    sx={{ fontWeight: 600 }}
                  >
                    Terms &amp; Conditions
                  </Link>{' '}
                  and{' '}
                  <Link
                    component="button"
                    type="button"
                    onClick={(e) => { e.preventDefault(); setLegalDoc('privacy'); }}
                    sx={{ fontWeight: 600 }}
                  >
                    Privacy Policy
                  </Link>
                </Typography>
              }
            />
            {errors.agreedToTerms && (
              <Typography variant="caption" sx={{ color: 'error.main', display: 'block', ml: 4, mt: -0.5 }}>
                {errors.agreedToTerms.message}
              </Typography>
            )}
          </Box>

          <Button
            type="submit"
            variant="contained"
            size="large"
            fullWidth
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : null}
            sx={{ height: 48, mt: 1 }}
          >
            {submitting ? 'Creating your workspace…' : 'Create account'}
          </Button>
        </Stack>
      </form>
    </AuthCard>

      <LegalDialog
        open={legalDoc !== null}
        doc={legalDoc}
        onClose={() => setLegalDoc(null)}
      />
    </>
  );
}
